import { AccountDetailsResponse } from '@/interfaces/clientProfile';
import {
	DATA_TYPE_LABELS,
	DATA_TYPE_VALUES,
	DataType,
	RequiredField,
	SignReviewComparison,
	SignReviewField,
	SignReviewFieldStatus,
	SignReviewProfileItem,
} from '@/interfaces';

const NOT_VALIDATED_TYPES: DataType[] = ['DO_NOT_VALIDATE', 'SIGNATURE'];

const EMPTY_COMPARISON: SignReviewComparison = {
	fields: [],
	profileItems: [],
	signature: null,
	mismatchedLabels: [],
	isComparisonAvailable: false,
};

const toDataType = (type: RequiredField['type']): DataType | null =>
	DATA_TYPE_VALUES.includes(type as DataType) ? (type as DataType) : null;

interface ReviewFieldsSource {
	required_fields?: RequiredField[];
	review_fields?: RequiredField[];
	signature?: string | null;
}

/** Documents sent with the ClaimUSign flow carry `match` on their `required_fields`. */
export const hasSignReviewComparison = (
	details: ReviewFieldsSource | undefined
): boolean =>
	(details?.required_fields ?? []).some((field) => field.match === true);

/** ClaimUSign documents send their reviewable fields in `review_fields`. */
export const getReviewSourceFields = (
	details: ReviewFieldsSource | undefined
): RequiredField[] =>
	(hasSignReviewComparison(details)
		? details?.review_fields
		: details?.required_fields) ?? [];

const getFieldStatus = (
	field: RequiredField,
	dataType: DataType | null
): SignReviewFieldStatus => {
	if (dataType && NOT_VALIDATED_TYPES.includes(dataType))
		return 'not_validated';
	if (field.match === true) return 'match';
	if (field.match === false) return 'mismatch';
	return 'unavailable';
};

const locationName = (value: { name?: string } | null | undefined): string =>
	value?.name?.trim() ?? '';

const profileValueFor = (
	dataType: DataType,
	profile: AccountDetailsResponse
): string => {
	const firstName = profile.firstname?.trim() ?? '';
	const lastName = profile.lastname?.trim() ?? '';
	const address1 = profile.address1?.trim() ?? '';
	const address2 = profile.address2?.trim() ?? '';
	const city = locationName(profile.city);
	const state = locationName(profile.state);
	const zip = profile.zip_code?.trim() ?? '';

	switch (dataType) {
		case 'FIRST_NAME':
			return firstName;
		case 'LAST_NAME':
			return lastName;
		case 'FULL_NAME':
			return [firstName, lastName].filter(Boolean).join(' ');
		case 'PHONE':
			return profile.phonenumber?.trim() ?? '';
		case 'EMAIL':
			return profile.email?.trim() ?? '';
		case 'ADDRESS_1':
			return address1;
		case 'ADDRESS_2':
			return address2;
		case 'FULL_ADDRESS':
			return [address1, address2, city, state, zip].filter(Boolean).join(', ');
		case 'CITY':
			return city;
		case 'STATE':
			return state;
		case 'ZIP_CODE':
			return zip;
		default:
			return '';
	}
};

const buildProfileItems = (
	profile: AccountDetailsResponse | undefined
): SignReviewProfileItem[] => {
	if (!profile) return [];
	return DATA_TYPE_VALUES.filter(
		(dataType) => !NOT_VALIDATED_TYPES.includes(dataType)
	).map((dataType) => ({
		label: DATA_TYPE_LABELS[dataType],
		value: profileValueFor(dataType, profile) || null,
	}));
};

export const buildSignReviewComparison = (
	details: ReviewFieldsSource | undefined,
	profile?: AccountDetailsResponse
): SignReviewComparison => {
	if (!details) return EMPTY_COMPARISON;

	const isComparisonAvailable = hasSignReviewComparison(details);

	const fields = getReviewSourceFields(details)
		.filter((field) => field.type !== 'SIGNATURE')
		.map((field): SignReviewField => {
			const dataType = toDataType(field.type);
			return {
				id: field.question_id,
				label: field.question,
				dataType,
				submittedValue: field.response,
				status: isComparisonAvailable
					? getFieldStatus(field, dataType)
					: 'unavailable',
			};
		});

	const hasSignature = !!details.signature && details.signature !== 'null';

	return {
		fields,
		profileItems: isComparisonAvailable ? buildProfileItems(profile) : [],
		signature: hasSignature ? details.signature ?? null : null,
		mismatchedLabels: fields
			.filter((field) => field.status === 'mismatch')
			.map((field) => field.label),
		isComparisonAvailable,
	};
};
